<script setup lang="ts">
import Password from '@/components/Password.vue';
import Testimonial from '@/components/Testimonial.vue';
import { useI18n } from '@/i18n';
import { isInviteRole, shouldSkipTrial } from '@/data/roles';
import { setAuthEmbeddedReturn } from '@/lib/authEmbedded';
import { redirect } from '@/lib/redirect';
import { createSignupSchema, type SignupFormValues } from '@/schemas/signup';
import type { SignUpOptions } from '@/stores/auth';
import { useAuthStore } from '@/stores/auth';
import { useBillingStore } from '@/stores/billing';
import { useUserStore } from '@/stores/user';
import { Alert, Button, Form, Heading, Input, LayoutGap, Loader } from '@dabble/quillui';
import AuthLayout from '@/components/AuthLayout.vue';
import InviteBanner from '@/components/InviteBanner.vue';
import { toTypedSchema } from '@vee-validate/yup';
import { useForm, useIsFormDirty } from 'vee-validate';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const route = useRoute();
const router = useRouter();
const { t, te } = useI18n();

function translateFieldError(raw: string | undefined) {
  if (!raw) return '';
  return te(raw) ? t(raw) : raw;
}
const authStore = useAuthStore();
const userStore = useUserStore();
const billingStore = useBillingStore();

const error = ref<string | undefined>(undefined);
const referralCode = ref('');
const isSubmitting = ref(false);

const rawSignupSchema = computed(() => createSignupSchema((key: string) => t(key)));
const validationSchema = computed(() => toTypedSchema(rawSignupSchema.value));

const { handleSubmit, defineField, values } = useForm<SignupFormValues>({
  validationSchema,
  initialValues: {
    name: '',
    email: '',
    password: '',
  },
});

/** `useIsFormValid()` can stay true before vee runs validation; mirror the Yup schema for the button + Quill Form. */
const isValid = computed(() =>
  rawSignupSchema.value.isValidSync({
    name: values.name ?? '',
    email: values.email ?? '',
    password: values.password ?? '',
  }),
);

const isDirty = useIsFormDirty();

const fieldOpts = {
  validateOnBlur: true,
  validateOnChange: true,
  validateOnModelUpdate: true,
  props: (state: { errors: string[] }) => ({
    error: state.errors.length > 0,
    errorMessage: translateFieldError(state.errors[0]),
  }),
};

const [name, nameField] = defineField('name', fieldOpts);
const [email, emailField] = defineField('email', fieldOpts);
const [password, passwordField] = defineField('password', fieldOpts);

function readQueryOrSession(key: string): string {
  const q = route.query[key];
  if (q !== undefined && q !== null && q !== '') {
    return String(Array.isArray(q) ? q[0] : q).trim();
  }
  const s = sessionStorage.getItem(key);
  return s ? String(s).trim() : '';
}

const isTrialNone = computed(() => readQueryOrSession('trial').toLowerCase() === 'none');

/** Invited collaborator (beta reader / editor): show "free account" copy, not the trial pitch. */
const isInvited = computed(() => isInviteRole(readQueryOrSession('role')));

function clearTrialRoleSessionKeys() {
  sessionStorage.removeItem('trial');
  sessionStorage.removeItem('role');
}

async function createAccount(values: SignupFormValues) {
  referralCode.value =
    readQueryOrSession('referralCode') || (typeof route.query.ref === 'string' ? route.query.ref : '') || '';
  const role = readQueryOrSession('role') || undefined;
  const trialNone = isTrialNone.value || shouldSkipTrial(role);

  const signUpOptions: SignUpOptions | undefined =
    role || !trialNone ? { ...(role ? { role } : {}), ...(!trialNone ? { trial: true as const } : {}) } : undefined;

  const response = await authStore.signUp(values.name, values.email, values.password, signUpOptions);

  if (response && response.user) {
    userStore.setUser(response.user);
    clearTrialRoleSessionKeys();

    if (trialNone) {
      sessionStorage.removeItem('referralCode');
      await redirect();
      return;
    }

    // Stripe customer + trial were provisioned server-side as part of
    // `/auth/user`'s signup tasks. Sync local billing state so downstream
    // views (and the coupon flow below) see the new subscription.
    await billingStore.fetchBillingOverview();

    if (referralCode.value) {
      await billingStore.applyCouponCode(referralCode.value);
    }
    sessionStorage.removeItem('referralCode');

    setAuthEmbeddedReturn();
    await router.push('/account/onboarding');
  }
}

const onSubmit = handleSubmit(async (values) => {
  error.value = undefined;
  isSubmitting.value = true;
  try {
    await createAccount(values);
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : String(e);
    error.value = te(message) ? t(message) : message;
  } finally {
    isSubmitting.value = false;
  }
});
</script>

<template>
  <AuthLayout :show-logo="false">
    <template #header>
      <div class="signup__logo-mobile">
        <img src="/images/dabble-logo-inverted.svg" alt="Dabble" />
      </div>
    </template>

    <template #aside>
      <div class="signup__intro">
        <div class="signup__logo">
          <img src="/images/dabble-logo-inverted.svg" alt="Dabble" />
        </div>
        <Heading level="title" family="secondary" class="signup__blurb" no-margin>{{
          t('create_account_blurb')
        }}</Heading>
        <Testimonial />
      </div>
    </template>

    <InviteBanner />

    <Form v-model:dirty="isDirty" :valid="isValid" :actionless="true" class="signup__form" @submit="onSubmit">
      <LayoutGap direction="column" size="medium" align="center" class="signup__form-contents">
        <LayoutGap direction="column" size="tiny" align="center">
          <Heading :level="3" no-margin>{{ t('create_account') }}</Heading>
          <Prose v-if="!isInvited">{{ t('create_account_intro') }}</Prose>
        </LayoutGap>
        <div role="alert" aria-live="assertive" aria-atomic="true">
          <Alert v-if="error" variant="warning">{{ t(error) }}</Alert>
        </div>
        <div v-if="isSubmitting" class="signup__loader">
          <Loader />
        </div>
        <LayoutGap v-else direction="column" align="center" size="small" class="signup__form-stack">
          <Input
            id="name"
            v-model="name"
            class="auth-field"
            v-bind="nameField"
            :label="t('name')"
            type="text"
            required
            size="large"
            :placeholder="t('signup_placeholder_name')"
          />
          <Input
            id="email"
            v-model="email"
            class="auth-field"
            v-bind="emailField"
            :label="t('email')"
            type="text"
            required
            size="large"
            :placeholder="t('signup_placeholder_email')"
          />
          <Password
            v-model="password"
            v-bind="passwordField"
            :placeholder="t('signup_placeholder_password')"
            show-requirements
            :allow-empty="false"
            class="signup__password auth-field"
          />
          <Button
            class="signup__submit"
            fill-width
            size="large"
            :disabled="authStore.signingUp || !isValid"
            type="submit"
          >
            {{ t('get_started') }}
          </Button>
        </LayoutGap>
      </LayoutGap>
    </Form>

    <template #footer>
      <p class="signup__login-link">
        {{ t('have_account') }}
        <router-link to="/auth/login">{{ t('login') }}</router-link>
      </p>
    </template>
  </AuthLayout>
</template>

<style lang="scss" scoped>
@use '@/styles/auth-form.scss';

.signup {
  &__intro {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: space('large');
    width: 432px;
    max-width: 100%;
  }

  &__logo {
    align-self: flex-start;
    max-width: 100%;

    img {
      display: block;
      max-width: 100%;
      max-height: 112px;
      width: auto;
      height: auto;
      object-fit: contain;
      filter: drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.1));
    }
  }

  &__blurb {
    align-self: flex-start;
    text-align: left;
    color: theme('color-text-primary-inverted');
    filter: drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.2));
  }

  &__form {
    width: 100%;
  }

  &__form-stack,
  &__password {
    width: 100%;
  }

  &__loader {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: space('huge') 0;
  }

  &__login-link {
    margin: 0;
  }

  &__submit {
    margin-top: space('medium');
  }

  &__logo-mobile img {
    display: block;
    height: 64px;
    width: auto;
    max-width: 100%;
    object-fit: contain;
    filter: drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.1));
  }
}

// Desktop: the logo lives in the aside column, so hide the top header logo.
:deep(.auth-layout__header) {
  display: none;
}

// Mobile: drop the byline + testimonial column and show the logo at the top instead.
@include media-query-below('tablet') {
  :deep(.auth-layout__aside) {
    display: none;
  }

  :deep(.auth-layout__header) {
    display: flex;
  }
}
</style>
